import axios from 'axios';
import { getBackendUrl } from './runtimeConfig';

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API,
  // 30s timeout gives production DB queries (analytics, etc.) enough time
  // without making the UI feel frozen for too long.
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  // Don't override if Authorization is already explicitly set by the caller
  if (config.headers.Authorization) {
    return config;
  }
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry interceptor: auto-retry on transient errors (network / 502 / 503 only).
// 504 Gateway Timeout is NOT retried — if the upstream timed out, a client-side
// retry will almost certainly time out again and just multiplies the wait.
// Max 2 retries to keep worst-case load time reasonable.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    // Dead admin session (expired, revoked by a password change, or signed
    // with a rotated secret): clear it and go back to the login page instead
    // of leaving the admin panel failing with "Invalid token". Other 401s
    // (e.g. "Current password is incorrect") are left alone.
    const detail = error.response?.data?.detail;
    const adminToken = localStorage.getItem('adminToken');
    if (
      status === 401 && adminToken &&
      config?.headers?.Authorization === `Bearer ${adminToken}` &&
      ['Invalid token', 'Token expired', 'Not authenticated'].includes(detail) &&
      window.location.pathname.startsWith('/admin/')
    ) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminName');
      localStorage.removeItem('adminEmail');
      window.location.assign('/admin');
      return Promise.reject(error);
    }

    // Retry only on true transient errors (502 Bad Gateway, 503 Unavailable)
    // or network errors where no HTTP status was returned.
    const isRetryable = !status || status === 502 || status === 503;

    if (isRetryable && config && !config._retryCount) {
      config._retryCount = 0;
    }

    if (isRetryable && config && config._retryCount < 2) {
      config._retryCount += 1;
      const delay = Math.min(2000 * config._retryCount, 4000);
      await new Promise((r) => setTimeout(r, delay));
      return api(config);
    }

    return Promise.reject(error);
  }
);

// Health check (used by App.js boot screen)
export const checkHealth = () =>
  axios.get(`${API}/health`, { timeout: 5000 });

// Auth
export const loginAdmin = (email, password, remember = true) =>
  api.post('/admin/login', { email, password, remember });

export const registerAdmin = (email, password, name) => 
  api.post('/admin/register', { email, password, name });

export const getAdminProfile = () => 
  api.get('/admin/me');

export const changeAdminPassword = (currentPassword, newPassword) =>
  api.post('/admin/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const updateAdminProfile = ({ name, email, currentPassword }) =>
  api.put('/admin/profile', {
    ...(name !== undefined ? { name } : {}),
    ...(email !== undefined ? { email } : {}),
    ...(currentPassword ? { current_password: currentPassword } : {}),
  });

// Wineries
export const getWineries = (params = {}) => 
  api.get('/wineries', { params });

export const getWineryCount = () => 
  api.get('/wineries/count');

export const getWinery = (id) => 
  api.get(`/wineries/${id}`);

export const createWinery = (data) => 
  api.post('/wineries', data);

export const updateWinery = (id, data) => 
  api.put(`/wineries/${id}`, data);

export const deleteWinery = (id) => 
  api.delete(`/wineries/${id}`);

export const reorderWineries = (items) => 
  api.put('/wineries/reorder', { items });

// Blog
export const getBlogPosts = (params = {}) => 
  api.get('/blog', { params });

export const getBlogPost = (slug) => 
  api.get(`/blog/${slug}`);

export const createBlogPost = (data) => 
  api.post('/blog', data);

export const updateBlogPost = (id, data) => 
  api.put(`/blog/${id}`, data);

export const deleteBlogPost = (id) => 
  api.delete(`/blog/${id}`);

export const reorderBlogPosts = (items) => 
  api.put('/blog/reorder', { items });

// Deals
export const getDeals = (params = {}) => 
  api.get('/deals', { params });

export const createDeal = (data) => 
  api.post('/deals', data);

export const updateDeal = (id, data) => 
  api.put(`/deals/${id}`, data);

export const deleteDeal = (id) => 
  api.delete(`/deals/${id}`);

export const reorderDeals = (items) => 
  api.put('/deals/reorder', { items });

// Contact & Newsletter
export const submitContact = (data) => 
  api.post('/contact', data);

export const getContactMessages = () => 
  api.get('/contact');

export const subscribeNewsletter = (email) => 
  api.post('/newsletter', { email });

export const getNewsletterSubscribers = () => 
  api.get('/newsletter');

// Settings
export const getSettings = () => 
  api.get('/settings');

export const updateSettings = (data) => 
  api.put('/settings', data);

// Admin Stats
export const getAdminStats = () => 
  api.get('/admin/stats');

// Sitemap
export const getSitemap = () => 
  api.get('/sitemap');

// Seed Data
export const seedData = () => 
  api.post('/seed');

export default api;
